import { z } from "zod";
import type {
  OrganizationalRecordSubject,
  OrganizationalRecordSubjectKind,
} from "@/pages/organizational-record/organizational-record-subject";

// A draft entry for the organizational record of a person or a company
// (@external-adr GENESIS-0035-organizational-records-are-a-global-capability,
// @external-adr GENESIS-0049-an-organisational-record-is-about-a-person-or-a-company), proposed by the assistant in
// the Studio and filed by the user from its card. The subject is picked in the card from search hits, never taken
// from the model; `filed` records what the user filed, so the notebook shows it after a reload.
export type OrgRecordEntryKind = "NOTE" | "REPORT";

export interface OrgRecordFiled {
  subject: OrganizationalRecordSubject;
  name: string;
  entryId: string;
  documentId?: string;
}

export interface OrgRecordEntryDraft {
  // Whose record, as the conversation names them: a person or a company, never both (genesis takes exactly one).
  subject: { kind: OrganizationalRecordSubjectKind; name: string };
  kind: OrgRecordEntryKind;
  title: string;
  content: string;
  attachNotebook: boolean;
  // The person or company the user picked from the hits.
  subjectId?: string;
  filed?: OrgRecordFiled;
}

const text = z.string().trim().min(1);

// The model's arguments: untrusted, and a card without exactly one subject, a title or a text is no card.
const argsSchema = z.object({
  personName: text.nullish(),
  companyName: text.nullish(),
  kind: z.enum(["NOTE", "REPORT"]).nullish(),
  title: text,
  content: text,
  attachNotebook: z.boolean().nullish(),
});

export function parseOrgRecordEntry(args: unknown): OrgRecordEntryDraft | null {
  const parsed = argsSchema.safeParse(args);
  if (!parsed.success) return null;
  const { personName, companyName, kind, title, content, attachNotebook } = parsed.data;
  if (Boolean(personName) === Boolean(companyName)) return null;
  const subject = personName ? { kind: "person" as const, name: personName } : { kind: "company" as const, name: companyName! };
  return { subject, kind: kind ?? "NOTE", title, content, attachNotebook: attachNotebook ?? false };
}

// The card as the notebook file keeps it, read back tolerantly. A card kept before 2026-10-02 names a person only
// (`personName`, `personId`, `filed.personId`); it reads as a person's draft.
export const orgRecordEntrySchema = z
  .object({
    subject: z.object({ kind: z.enum(["person", "company"]), name: z.string() }).optional(),
    personName: z.string().optional(),
    kind: z.enum(["NOTE", "REPORT"]).catch("NOTE"),
    title: z.string(),
    content: z.string(),
    attachNotebook: z.boolean().catch(false),
    subjectId: z.string().optional(),
    personId: z.string().optional(),
    filed: z
      .object({
        subject: z.object({ kind: z.enum(["person", "company"]), id: z.string() }).optional(),
        personId: z.string().optional(),
        name: z.string().optional(),
        personName: z.string().optional(),
        entryId: z.string(),
        documentId: z.string().optional(),
      })
      .optional(),
  })
  .transform((card): OrgRecordEntryDraft => {
    const subject = card.subject ?? { kind: "person" as const, name: card.personName ?? "" };
    const subjectId = card.subjectId ?? card.personId;
    const filed = card.filed;
    const filedSubject = filed?.subject ?? (filed?.personId ? { kind: "person" as const, id: filed.personId } : undefined);
    return {
      subject,
      kind: card.kind,
      title: card.title,
      content: card.content,
      attachNotebook: card.attachNotebook,
      ...(subjectId ? { subjectId } : {}),
      ...(filed && filedSubject
        ? {
            filed: {
              subject: filedSubject,
              name: filed.name ?? filed.personName ?? subject.name,
              entryId: filed.entryId,
              ...(filed.documentId ? { documentId: filed.documentId } : {}),
            },
          }
        : {}),
    };
  });
